/**
 * 로컬 플레이어 — 입력, 이동, 충돌, 카메라.
 * 서버는 이 결과(위치)를 보고받아 속도만 검증한다.
 */

import * as THREE from 'three';
import { PLAYER_R } from '../../shared/level';
import { ep as epData } from './episodeData';
import type { Collider } from './world';
import type { Ground } from './ground';

export const FLAG_MOVING = 1;
export const FLAG_CROUCH = 2;
export const FLAG_LIGHT = 8;

const WALK = 3.4;
const RUN = 5.6;
const CROUCH = 1.7;
const ACCEL = 26;

export class LocalPlayer {
  pos = new THREE.Vector3(0, 0, 9.5);
  /** 외부(터치 조이스틱) 이동 입력. x=좌우, y=앞뒤, 길이 0..1 */
  moveInput = { x: 0, y: 0 };
  /** 터치 환경에서는 포인터 락을 요구하지 않는다 */
  touchMode = false;
  /**
   * 자동 검증 전용: 충돌을 무시하고 목표 지점으로 이동한다.
   * 동기화 하네스는 '벽을 어떻게 돌아가는지'가 아니라 '상태가 맞는지'를 보므로
   * 경로 탐색 대신 이 플래그를 쓴다. 충돌 자체는 tools/collide-check.mjs 가 검증한다.
   */
  noclip = false;
  vel = new THREE.Vector3();
  yaw = Math.PI;
  pitch = 0;
  crouch = false;
  flashlight: THREE.SpotLight;
  thirdPerson = false;
  /**
   * **꺼 두면 입력을 안 받는다** (배치 에디터의 걷기 모드, 2026-09-26). 에디터는 평소엔 편집 입력을 쓰고 걷기일 때만
   * 이 플레이어를 켠다 — 꺼져 있는 동안 캔버스 클릭이 포인터 락을 잡거나 WASD 가 쌓이면 안 된다. 게임은 늘 켜져 있다
   */
  active = true;

  private keys = new Set<string>();
  private locked = false;
  private lightOn = false;

  constructor(
    private camera: THREE.PerspectiveCamera,
    canvas: HTMLCanvasElement,
    private colliders: Collider[],
    private scene: THREE.Scene,
    /**
     * 발이 딛는 높이를 아는 것. 없으면 예전처럼 y=0 에 붙어 다닌다 —
     * 단층 레벨(사흘)은 그래도 똑같이 동작한다
     */
    private ground?: Ground,
  ) {
    this.flashlight = new THREE.SpotLight('#fff4d6', 0, 16, Math.PI / 7, 0.45, 1.2);
    this.flashlight.castShadow = false;
    scene.add(this.flashlight);
    scene.add(this.flashlight.target);

    canvas.addEventListener('click', () => {
      // 터치 기기에는 포인터 락이 없다 (있어도 조작을 막는다)
      if (this.touchMode || !this.active) return;
      if (!this.locked && !document.body.classList.contains('modal-open')) {
        canvas.requestPointerLock();
      }
    });
    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === canvas;
      document.body.classList.toggle('locked', this.locked);
      if (!this.locked) this.keys.clear();
    });
    document.addEventListener('mousemove', (e) => {
      if (!this.locked || !this.active) return;
      const s = 0.0022;
      this.yaw -= e.movementX * s;
      this.pitch -= e.movementY * s;
      this.pitch = Math.max(-1.45, Math.min(1.45, this.pitch));
    });
    window.addEventListener('keydown', (e) => {
      if (isTyping() || !this.active) return;
      this.keys.add(e.code);
      if (e.code === 'KeyT') this.toggleLight();
      if (e.code === 'KeyV') this.thirdPerson = !this.thirdPerson;
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));
    window.addEventListener('blur', () => this.keys.clear());
  }

  /** 충돌체·바닥을 갈아 끼운다 — 에디터가 세계를 다시 지으면 배열이 새로 생긴다 */
  setWorld(colliders: Collider[], ground?: Ground) {
    this.colliders = colliders;
    if (ground) this.ground = ground;
  }

  get isLocked() {
    return this.locked || this.touchMode;
  }

  /** 시야 회전 (터치 드래그 / 자이로 등 외부 입력용). 단위는 라디안. */
  applyLook(dYaw: number, dPitch: number) {
    this.yaw -= dYaw;
    this.pitch = Math.max(-1.45, Math.min(1.45, this.pitch - dPitch));
  }

  setLight(on: boolean) {
    this.lightOn = on;
    this.flashlight.intensity = on ? 22 : 0;
  }

  get lightIsOn() {
    return this.lightOn;
  }

  unlock() {
    document.exitPointerLock();
  }

  toggleLight() {
    this.lightOn = !this.lightOn;
    this.flashlight.intensity = this.lightOn ? 22 : 0;
  }

  get flags() {
    let f = 0;
    if (this.vel.lengthSq() > 0.4) f |= FLAG_MOVING;
    if (this.crouch) f |= FLAG_CROUCH;
    if (this.lightOn) f |= FLAG_LIGHT;
    return f;
  }

  update(dt: number) {
    // 키보드와 터치 입력을 같은 축으로 합친다 (둘 다 붙어 있어도 자연스럽게 동작)
    const keyFwd = (this.keys.has('KeyW') ? 1 : 0) - (this.keys.has('KeyS') ? 1 : 0);
    const keyStrafe = (this.keys.has('KeyD') ? 1 : 0) - (this.keys.has('KeyA') ? 1 : 0);
    const fwd = clampUnit(keyFwd + this.moveInput.y);
    const strafe = clampUnit(keyStrafe + this.moveInput.x);
    this.crouch = this.keys.has('KeyC') || this.keys.has('ControlLeft');
    // 조이스틱을 끝까지 밀면 달린다 (터치에 별도 달리기 버튼을 두지 않기 위해)
    const stickMag = Math.hypot(this.moveInput.x, this.moveInput.y);
    const run = (this.keys.has('ShiftLeft') || stickMag > 0.92) && !this.crouch;
    const speed = this.crouch ? CROUCH : run ? RUN : WALK;

    // yaw 기준 이동 방향
    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    let dx = -sin * fwd + cos * strafe;
    let dz = -cos * fwd - sin * strafe;
    const len = Math.hypot(dx, dz);
    if (len > 0) {
      dx /= len;
      dz /= len;
    }

    const wantX = dx * speed;
    const wantZ = dz * speed;
    const k = Math.min(1, ACCEL * dt);
    this.vel.x += (wantX - this.vel.x) * k;
    this.vel.z += (wantZ - this.vel.z) * k;
    if (Math.abs(this.vel.x) < 0.01) this.vel.x = 0;
    if (Math.abs(this.vel.z) < 0.01) this.vel.z = 0;

    this.move(this.vel.x * dt, this.vel.z * dt);
    this.applyCamera(dt);
  }

  /**
   * 이동 후 겹친 만큼만 밀어낸다 (최소이동벡터).
   *
   * 예전에는 축별로 "겹치면 AABB 의 그 면 바깥으로 스냅" 했는데, 긴 벽 옆에 붙어
   * 걸으면 벽의 **끝 좌표로 순간이동**했다 (한 프레임에 20m 튐을 실측).
   * 겹친 깊이만큼만 밀어내면 벽을 스칠 때 위치가 거의 변하지 않는다.
   */
  private move(mx: number, mz: number) {
    const fromX = this.pos.x;
    const fromZ = this.pos.z;
    this.pos.x += mx;
    this.pos.z += mz;
    if (this.noclip) return;

    // 코너에서는 한 번 밀어낸 결과가 다른 벽과 다시 겹칠 수 있어 몇 번 반복한다
    for (let iter = 0; iter < 4; iter++) {
      let touched = false;
      for (const c of this.colliders) {
        if (!c.enabled) continue;
        if (this.pushOut(c)) touched = true;
      }
      if (!touched) break;
    }

    /**
     * 발밑 바닥을 따라간다.
     *
     * 낮은 턱(기단·툇마루·문지방)은 그냥 올라서고, 넘을 수 없는 높이면 **가려던
     * 자리를 되돌린다** — 벽에 막히는 것과 같은 결과다. 램프 위에서는 경사가
     * 높이를 정한다.
     *
     * 이게 없던 동안 `pos.y` 는 늘 0이었다. 3층짜리 집을 지어 놓고 지하로
     * 내려갈 수가 없었다.
     */
    if (!this.ground) return;
    const g = this.ground.at(this.pos.x, this.pos.z, this.pos.y);
    if (g === null) {
      // 딛을 것이 없다 — 허공이거나 넘지 못할 턱이다. 가지 않은 것으로 한다
      this.pos.x = fromX;
      this.pos.z = fromZ;
      return;
    }
    // 내려갈 때만 부드럽게. 올라설 때는 즉시여야 턱에 걸린 느낌이 안 난다
    this.pos.y = g < this.pos.y ? this.pos.y + (g - this.pos.y) * 0.35 : g;
    if (Math.abs(this.pos.y - g) < 0.005) this.pos.y = g;
  }

  /** 원 vs AABB. 겹쳤으면 밀어내고 벽 법선 방향 속도만 지운다(=벽을 따라 미끄러진다). */
  private pushOut(c: Collider): boolean {
    const px = this.pos.x;
    const pz = this.pos.z;

    if (c.circle) {
      // 원 vs 원 — 원기둥 가구는 옆으로 스치면 자연스럽게 돌아가야 한다
      const ex = px - c.circle.x;
      const ez = pz - c.circle.z;
      const rr = c.circle.r + PLAYER_R;
      const dd = ex * ex + ez * ez;
      if (dd >= rr * rr) return false;
      const d = Math.sqrt(dd) || 1e-6;
      const ux = ex / d;
      const uz = ez / d;
      const push = rr - d;
      this.pos.x += ux * push;
      this.pos.z += uz * push;
      const inTo = this.vel.x * ux + this.vel.z * uz;
      if (inTo < 0) {
        this.vel.x -= inTo * ux;
        this.vel.z -= inTo * uz;
      }
      return true;
    }

    const nearX = px < c.minX ? c.minX : px > c.maxX ? c.maxX : px;
    const nearZ = pz < c.minZ ? c.minZ : pz > c.maxZ ? c.maxZ : pz;
    let dx = px - nearX;
    let dz = pz - nearZ;
    const d2 = dx * dx + dz * dz;

    let nx: number;
    let nz: number;
    let depth: number;

    if (d2 > 1e-9) {
      // 원 중심이 상자 밖 — 가장 가까운 점에서 바깥으로
      if (d2 >= PLAYER_R * PLAYER_R) return false;
      const d = Math.sqrt(d2);
      nx = dx / d;
      nz = dz / d;
      depth = PLAYER_R - d;
    } else {
      // 중심이 상자 안까지 파고든 경우 — 가장 얕은 면으로 빼낸다
      const toMinX = px - c.minX;
      const toMaxX = c.maxX - px;
      const toMinZ = pz - c.minZ;
      const toMaxZ = c.maxZ - pz;
      const m = Math.min(toMinX, toMaxX, toMinZ, toMaxZ);
      if (m === toMinX) {
        nx = -1;
        nz = 0;
        depth = toMinX + PLAYER_R;
      } else if (m === toMaxX) {
        nx = 1;
        nz = 0;
        depth = toMaxX + PLAYER_R;
      } else if (m === toMinZ) {
        nx = 0;
        nz = -1;
        depth = toMinZ + PLAYER_R;
      } else {
        nx = 0;
        nz = 1;
        depth = toMaxZ + PLAYER_R;
      }
    }

    this.pos.x += nx * depth;
    this.pos.z += nz * depth;

    // 벽을 파고드는 속도 성분만 제거 — 축 전체를 0 으로 만들면 벽을 스칠 때 멈칫한다
    const into = this.vel.x * nx + this.vel.z * nz;
    if (into < 0) {
      this.vel.x -= into * nx;
      this.vel.z -= into * nz;
    }
    return true;
  }

  private bob = 0;
  private applyCamera(dt: number) {
    // 눈높이는 **사건이 정한다** — 한옥은 서양식 실내보다 낮다
    const eyeH = epData().eyeHeight;
    const eye = this.crouch ? eyeH * 0.62 : eyeH;
    const moving = this.vel.lengthSq() > 1;
    this.bob += dt * (moving ? this.vel.length() * 2.2 : 0);
    const bobY = moving ? Math.sin(this.bob) * 0.035 : 0;
    const bobX = moving ? Math.cos(this.bob * 0.5) * 0.02 : 0;

    // pos.y 는 발이 딛고 선 높이다. 단층 레벨에서는 늘 0 이라 동작이 같고,
    // 층이 있는 세트에서는 계단이 이 값을 올리고 내린다
    const head = new THREE.Vector3(this.pos.x, this.pos.y + eye + bobY, this.pos.z);
    if (this.thirdPerson) {
      const back = new THREE.Vector3(Math.sin(this.yaw), 0, Math.cos(this.yaw)).multiplyScalar(3.2);
      this.camera.position.copy(head).add(back).add(new THREE.Vector3(0, 0.7, 0));
    } else {
      this.camera.position.copy(head);
      this.camera.position.x += Math.cos(this.yaw) * bobX;
      this.camera.position.z -= Math.sin(this.yaw) * bobX;
    }
    this.camera.rotation.set(0, 0, 0);
    this.camera.rotateY(this.yaw);
    this.camera.rotateX(this.pitch);

    // 손전등은 시선을 따라간다
    this.flashlight.position.copy(head);
    const dir = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
    this.flashlight.target.position.copy(head).add(dir.multiplyScalar(10));
  }

  dispose() {
    this.scene.remove(this.flashlight, this.flashlight.target);
  }
}

function clampUnit(v: number) {
  return v < -1 ? -1 : v > 1 ? 1 : v;
}

export function isTyping() {
  const el = document.activeElement;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA');
}
